/**
 * AppUpdateGate — wraps the app tree and owns the two release surfaces.
 *
 * 1. The native version gate, served by GET /app/version-gate (detection is
 *    unchanged — see hooks/use-app-update.ts):
 *      • "forced"   → the app is replaced by a NON-dismissible UpdateSheet
 *                     ("What's new in CampusHub <latest>", "Update now" opens
 *                     the store listing). Users cannot proceed on a build
 *                     older than APP_MIN_BUILD.
 *      • "optional" → children render untouched. Deliberately NOT surfaced
 *                     (the slim banner was removed 2026-07-31); bumping
 *                     APP_LATEST_BUILD records the newest build without
 *                     interrupting anyone.
 *      • "ok"       → children render untouched (also the web/PWA path — the
 *                     service worker auto-updates the web bundle).
 *    Never render the forced sheet without a real store URL: a blocking
 *    screen whose only action is a no-op would strand the user.
 *
 * 2. "What's new" — once per version, after an update has landed (native or
 *    web), for the signed-in user's role. See WhatsNewHost below.
 */
import { useEffect, useState, type ReactNode } from "react";
import { whenNoOtherDialog } from "@/lib/when-no-other-dialog";
import { useAppUpdate, openStore } from "@/hooks/use-app-update";
import { useAuthStore } from "@/store/auth-store";
import { APP_VERSION } from "@/lib/runtime-env";
import { markWhatsNewSeen, releaseNotesFor, shouldShowWhatsNew } from "@/release-notes";
import { UpdateSheet } from "./update-sheet";

export function AppUpdateGate({ children }: { children: ReactNode }) {
  const update = useAppUpdate();
  const role = useAuthStore((s) => s.user?.role ?? null);

  // Dev-server only (dead code in every build): `?preview-update=1` shows the
  // forced sheet for the running version, so QA can see it in a browser —
  // the real gate only fires on a native shell below APP_MIN_BUILD.
  if (import.meta.env.DEV && typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("preview-update") === "1") {
    return (
      <UpdateSheet
        mode="update"
        version={APP_VERSION}
        entry={releaseNotesFor(APP_VERSION, role)}
        onPrimary={() => undefined}
        onLater={() => window.history.back()}
      />
    );
  }

  if (update.storeUrl && update.status === "forced") {
    // The notes a build can carry are its own; for the version on offer the
    // bundle may have none, and the sheet then shows its generic three.
    const entry = update.latestVersion ? releaseNotesFor(update.latestVersion, role) : null;
    return (
      <UpdateSheet
        mode="update"
        version={update.latestVersion}
        entry={entry}
        onPrimary={() => openStore(update.storeUrl)}
      />
    );
  }

  return (
    <>
      {children}
      <WhatsNewHost />
    </>
  );
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * "What's new in CampusHub <version>" — the first launch of a new version,
 * once, for a signed-in user, filtered to their role. A first install stays
 * silent (see shouldShowWhatsNew). Dismissing records the version.
 */
export function WhatsNewHost({ version = APP_VERSION }: { version?: string }) {
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!releaseNotesFor(version, user.role)) return;
    if (!shouldShowWhatsNew(version, safeStorage())) return;
    // Queue behind the first-run prompts (AI & Privacy, device trust, PIN,
    // tour, forced notices) instead of opening on top of them.
    return whenNoOtherDialog(1500, () => setOpen(true));
  }, [user, version]);

  if (!open || !user) return null;
  const entry = releaseNotesFor(version, user.role);
  if (!entry) return null;
  return (
    <UpdateSheet
      mode="whats-new"
      version={version}
      entry={entry}
      onPrimary={() => {
        markWhatsNewSeen(version, safeStorage());
        setOpen(false);
      }}
    />
  );
}
